import { z } from "zod";
// ============================================================================
// CONSTANTS
// ============================================================================
export const VOCAB_CATEGORIES = [
    "appearance", "personality", "emotions", "family", "food", "drinks",
    "housing", "household", "travel", "transport", "work", "professions",
    "health", "body", "numbers", "time", "clothing", "animals", "nature",
    "weather", "education", "grammar", "sports", "technology", "shopping",
    "communication", "movement", "daily_routine", "hobbies", "music",
    "colors", "greetings", "relationships", "society", "geography",
    "culture", "places", "law", "other",
];
// ============================================================================
// ENUMS
// ============================================================================
export const WordTypeEnum = z.enum([
    "noun", "verb", "adjective", "adverb", "preposition", "conjunction", "phrase", "other"
]);
export const CefrLevelEnum = z.enum(["A1", "A2", "B1", "B2", "C1", "C2"]);
export const GenderEnum = z.enum(["der", "die", "das"]);
// ============================================================================
// VOCAB WORD SCHEMAS
// ============================================================================
export const VerbFormsSchema = z.object({
    present: z.string(),
    past: z.string(),
    perfect: z.string(),
});
export const TranslationsSchema = z.object({
    en: z.string(),
    uk: z.string(),
    ru: z.string(),
});
export const ExampleSentencesSchema = z.object({
    de: z.string(),
    en: z.string(),
    uk: z.string(),
    ru: z.string(),
});
/** Full vocab word shape (admin response / DB item) */
export const VocabWordSchema = z.object({
    id: z.number().int(),
    module_id: z.number().int(),
    order: z.number().int(),
    german: z.string(),
    translations: TranslationsSchema,
    example: ExampleSentencesSchema,
    word_type: WordTypeEnum,
    category: z.string(),
    cefr_level: CefrLevelEnum,
    gender: GenderEnum.nullable().optional(),
    plural: z.string().nullable().optional(),
    verb_forms: VerbFormsSchema.nullable().optional(),
    audio_url: z.string().nullable().optional(),
    created_at: z.string(),
    updated_at: z.string(),
});
/** Single-language view for learners (server picks preferred_language) */
export const VocabWordTranslatedSchema = z.object({
    id: z.number().int(),
    module_id: z.number().int(),
    order: z.number().int(),
    german: z.string(),
    translation: z.string(),
    example_de: z.string(),
    example_translated: z.string(),
    word_type: WordTypeEnum,
    category: z.string(),
    cefr_level: CefrLevelEnum,
    gender: GenderEnum.nullable().optional(),
    plural: z.string().nullable().optional(),
    verb_forms: VerbFormsSchema.nullable().optional(),
    audio_url: z.string().nullable().optional(),
    in_user_vocab: z.boolean().optional(),
    course_id: z.number().int().optional(),
    exercise_id: z.number().int().nullable().optional(),
});
// ============================================================================
// CRUD PAYLOADS
// ============================================================================
/** Bulk create words from AI extraction (after admin review) */
export const CreateVocabWordsPayloadSchema = z.object({
    words: z.array(z.object({
        german: z.string(),
        translations: TranslationsSchema,
        example: ExampleSentencesSchema,
        word_type: WordTypeEnum,
        category: z.string(),
        cefr_level: CefrLevelEnum,
        gender: GenderEnum.nullable().optional(),
        plural: z.string().nullable().optional(),
        verb_forms: VerbFormsSchema.nullable().optional(),
    })),
});
/** Partial update for a single word */
export const UpdateVocabWordPayloadSchema = z.object({
    german: z.string().optional(),
    translations: TranslationsSchema.partial().optional(),
    example: ExampleSentencesSchema.partial().optional(),
    word_type: WordTypeEnum.optional(),
    category: z.string().optional(),
    cefr_level: CefrLevelEnum.optional(),
    gender: GenderEnum.nullable().optional(),
    plural: z.string().nullable().optional(),
    verb_forms: VerbFormsSchema.nullable().optional(),
    audio_url: z.string().nullable().optional(),
});
// ============================================================================
// FSRS CARD STATE ENUM
// ============================================================================
/** FSRS card state: 0=New, 1=Learning, 2=Review, 3=Relearning */
export const FsrsStateEnum = z.number().int().min(0).max(3);
// ============================================================================
// USER VOCAB / SRS SCHEMAS
// ============================================================================
/** FSRS card state for a word in user's personal set */
export const UserVocabSchema = z.object({
    word_id: z.number().int(),
    module_id: z.number().int(),
    // FSRS card state
    stability: z.number(),
    difficulty: z.number(),
    elapsed_days: z.number(),
    scheduled_days: z.number(),
    learning_steps: z.number().int(),
    reps: z.number().int(),
    lapses: z.number().int(),
    state: FsrsStateEnum,
    next_review_at: z.string(),
    last_reviewed_at: z.string().nullable(),
    // Stats (not part of FSRS, but useful for analytics)
    total_reviews: z.number().int(),
    correct_reviews: z.number().int(),
    added_at: z.string(),
    source: z.enum(["module", "exercise", "custom"]),
    context_sentence: z.string().nullable().optional(),
    context_translation: z.string().nullable().optional(),
    // Personal overrides (learner edits)
    personal_translation: z.string().nullable().optional(),
    personal_example_de: z.string().nullable().optional(),
    personal_example_translated: z.string().nullable().optional(),
    personal_word_type: WordTypeEnum.nullable().optional(),
    personal_category: z.string().nullable().optional(),
    personal_cefr_level: CefrLevelEnum.nullable().optional(),
    personal_gender: GenderEnum.nullable().optional(),
    personal_plural: z.string().nullable().optional(),
    personal_verb_forms: VerbFormsSchema.nullable().optional(),
});
/** Joined: SRS state + word data (for learner "My Vocabulary" view) */
export const UserVocabWithWordSchema = UserVocabSchema.extend({
    german: z.string(),
    translation: z.string(),
    example_de: z.string(),
    example_translated: z.string(),
    word_type: WordTypeEnum,
    category: z.string(),
    cefr_level: CefrLevelEnum,
    gender: GenderEnum.nullable().optional(),
    plural: z.string().nullable().optional(),
    verb_forms: VerbFormsSchema.nullable().optional(),
    audio_url: z.string().nullable().optional(),
    has_overrides: z.boolean().optional(),
    exercise_id: z.number().int().nullable().optional(),
    course_id: z.number().int().optional(),
    exercise_slug: z.string().nullable().optional(),
    module_slug: z.string().nullable().optional(),
    course_slug: z.string().nullable().optional(),
});
/** Payload for learner to update personal overrides on a word */
export const UpdateUserVocabOverridesPayloadSchema = z.object({
    source: z.enum(["module", "exercise", "custom"]),
    personal_translation: z.string().nullable().optional(),
    personal_example_de: z.string().nullable().optional(),
    personal_example_translated: z.string().nullable().optional(),
    personal_word_type: WordTypeEnum.nullable().optional(),
    personal_category: z.string().nullable().optional(),
    personal_cefr_level: CefrLevelEnum.nullable().optional(),
    personal_gender: GenderEnum.nullable().optional(),
    personal_plural: z.string().nullable().optional(),
    personal_verb_forms: VerbFormsSchema.nullable().optional(),
});
// ============================================================================
// REVIEW / FSRS PAYLOADS
// ============================================================================
/** Submit review result (FSRS rating: 1=Again, 2=Hard, 3=Good, 4=Easy) */
export const VocabReviewPayloadSchema = z.object({
    rating: z.number().int().min(1).max(4),
});
/** Response with updated FSRS card state after review */
export const VocabReviewResultSchema = z.object({
    word_id: z.number().int(),
    stability: z.number(),
    difficulty: z.number(),
    elapsed_days: z.number(),
    scheduled_days: z.number(),
    learning_steps: z.number().int(),
    reps: z.number().int(),
    lapses: z.number().int(),
    state: FsrsStateEnum,
    next_review_at: z.string(),
    last_reviewed_at: z.string(),
    total_reviews: z.number().int(),
    correct_reviews: z.number().int(),
});
/** Payload to revert FSRS state (undo last review) */
export const VocabRevertSrsPayloadSchema = z.object({
    stability: z.number(),
    difficulty: z.number(),
    elapsed_days: z.number(),
    scheduled_days: z.number(),
    learning_steps: z.number().int(),
    reps: z.number().int(),
    lapses: z.number().int(),
    state: FsrsStateEnum,
    next_review_at: z.string(),
    last_reviewed_at: z.string().nullable(),
    total_reviews: z.number().int(),
    correct_reviews: z.number().int(),
});
// ============================================================================
// AI TRANSCRIPT EXTRACTION
// ============================================================================
/** Payload for AI transcript extraction */
export const TranscriptExtractPayloadSchema = z.object({
    transcript: z.string().min(1).max(50000),
    module_id: z.number().int(),
});
/** Result of AI transcript extraction (preview before save) */
export const TranscriptExtractResultSchema = z.object({
    summary: z.string(),
    words: z.array(z.object({
        german: z.string(),
        translations: TranslationsSchema,
        example: ExampleSentencesSchema,
        word_type: WordTypeEnum,
        category: z.string(),
        cefr_level: CefrLevelEnum,
        gender: GenderEnum.nullable().optional(),
        plural: z.string().nullable().optional(),
        verb_forms: VerbFormsSchema.nullable().optional(),
    })),
});
/** Payload for adding a custom word (with optional exercise context) */
export const AddCustomWordPayloadSchema = z.object({
    word: z.string().min(1).max(100),
    context: z.string().max(1000).optional(),
    context_sentence: z.string().max(500).optional(),
    context_translation: z.string().max(500).optional(),
    exercise_id: z.number().optional(),
    module_id: z.number().optional(),
    course_id: z.number().optional(),
    // Optional enrichment hints from TRANS cache (avoids second GPT call)
    hint_cefr_level: z.string().optional(),
    hint_category: z.string().optional(),
    hint_part_of_speech: z.string().optional(),
    hint_gender: z.string().nullable().optional(),
    hint_plural: z.string().nullable().optional(),
    hint_present_er: z.string().nullable().optional(),
    hint_prateritum: z.string().nullable().optional(),
    hint_perfekt: z.string().nullable().optional(),
    hint_translation: z.string().optional(),
    hint_word_base_form: z.string().optional(),
});
/** Payload for enriching a single word from exercise context */
export const EnrichWordPayloadSchema = z.object({
    word: z.string().min(1).max(100),
    context: z.string().max(1000).optional(),
});
/** Result of word enrichment */
export const EnrichWordResultSchema = z.object({
    german: z.string().max(200),
    translations: TranslationsSchema,
    example: ExampleSentencesSchema,
    word_type: WordTypeEnum,
    category: z.string(),
    cefr_level: CefrLevelEnum,
    gender: GenderEnum.nullable().optional(),
    plural: z.string().nullable().optional(),
    verb_forms: VerbFormsSchema.nullable().optional(),
});
/** Payload for saving a pre-enriched custom word */
export const SaveEnrichedWordPayloadSchema = EnrichWordResultSchema;
/** Payload for bulk-importing enriched custom words (POST /vocab/my/bulk-import) */
export const BulkImportWordsPayloadSchema = z.object({
    words: z.array(SaveEnrichedWordPayloadSchema).min(1).max(1000),
    skip_existing: z.boolean().optional(),
});
/** Per-word outcome in a bulk-import response */
export const BulkImportSkippedSchema = z.object({
    german: z.string(),
    reason: z.string(),
});
export const BulkImportFailedSchema = z.object({
    german: z.string(),
    error: z.string(),
});
/** Response from POST /vocab/my/bulk-import */
export const BulkImportWordsResponseSchema = z.object({
    added: z.array(z.number()),
    skipped: z.array(BulkImportSkippedSchema),
    failed: z.array(BulkImportFailedSchema),
    summary: z.object({
        total: z.number(),
        added: z.number(),
        skipped: z.number(),
        failed: z.number(),
    }),
});
